import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DeleteButton, ConfirmationBox } from '../../shared';
import {
    AUTH,
    HEADERS,
    INFO,
    isAdminRole,
    isCSMRole,
    MAX_LENGTH,
    QUERY_KEYS,
    ROLES,
    ROUTES,
    uptoContentManagerRole,
    USER,
} from '../../../constants';
import {
    popup,
    createAccountUser,
    deleteAccountUser,
    impersonateUser,
    patchAccountUserIsPrimaryAPI,
    updateUsersStatusAPI,
    initiateResetPassword,
    getAllUsers,
} from '../../../utils';
import { CreateUser } from './CreateUser';
import {
    CircleSpinner,
    Delete,
    Edit,
    Email,
    IconPlus,
    ImpersonateUserIcon,
    ProfileIcon,
    SettingIcon,
    User,
} from '../../../assets/svg';
import { formatDate } from '../../../utils/common/formatHelpers';
import useWindowSize from '../../../hooks/useWindowSize';
import SSCSelect from '../../shared/SSCSelect';
import SSCToggle from '../../shared/Buttons/SSCToggle';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import NotificationSettings from '../../shared/common/NotificationSettings';
import { Modal } from 'react-bootstrap';

const getTableHeader = (editUser) =>
    editUser
        ? ['', '#', 'Name', 'Email', 'Last Logged In', 'Type', 'Action']
        : ['#', 'Name', 'Email', 'Last Logged In', 'Type', 'Status', 'Action'];

export const ManageUserBox = ({ data }) => {
    const accountId = data?.account?.account_id;
    const queryClient = useQueryClient();
    const role = useSelector((s) => s?.user?.role);
    const allowedImpersonate = [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.CSM,
        ROLES.SALES_AGENT,
    ].includes(role);
    const [editUser, setEditUser] = useState(false);
    const [newUser, setNewUser] = useState(false);
    const [selectUser, setSelectedUser] = useState('');
    const [userSettingData, setUserSettingData] = useState(undefined);
    const [addNewUser, setAddNewUser] = useState('');
    const [searchUser, setSearchUser] = useState('');
    const [users, setUsers] = useState(data?.account?.users || []);
    const [checkedUsers, setCheckedUsers] = useState([]);
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
    const [openIsPrimaryBox, setOpenIsPrimaryBox] = useState(false);
    const [openUserSetting, setOpenUserSetting] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [primaryUsers, setPrimaryUsers] = useState(
        users.filter((user) => user.is_primary === 1).map((user) => user.id) || [],
    );
    const [changesMade, setChangesMade] = useState(false);
    const { width } = useWindowSize();
    const isSmallScreen = width < 720;
    const [debouncedSearch] = useDebouncedValue(searchUser, 500);
    const {
        data: userOptions,
        isError,
        error,
        isFetching,
    } = useQuery({
        queryKey: [QUERY_KEYS.USER_USER, debouncedSearch, '25'],
        queryFn: async () =>
            await getAllUsers({
                limit: 25,
                offset: 1,
                search: debouncedSearch,
                role: [ROLES.USER],
            }),
        select: (data) =>
            data?.data?.map((user) => ({
                value: user,
                label: user.full_name,
            })),
        placeholderData: [],
        enabled: editUser,
    });

    if (isError) popup('error', error?.message);

    const handleAddSelectedUser = (user) => {
        if (user) {
            if (users.some((u) => u.id === user.id)) {
                popup('error', `${user.full_name} already exist`);
            } else {
                setUsers((prevUsers) => [...prevUsers, user]);
                popup('info', `Added ${user.full_name}`);
                setSelectedUser('');
                setChangesMade(true);
            }
        }
    };

    useEffect(() => {
        if (addNewUser) {
            handleAddSelectedUser(addNewUser);
            setAddNewUser('');
        } else if (selectUser) {
            handleAddSelectedUser(selectUser);
            setSelectedUser(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addNewUser, selectUser]);

    const handleSetPrimary = async () => {
        if (!isEmpty(checkedUsers) && checkedUsers.length <= MAX_LENGTH.PRIMARY_USERS) {
            const checkedUsersIds = checkedUsers.map((user) => user.id);
            setPrimaryUsers(checkedUsersIds);
            setUsers(
                users.map((u) => ({
                    ...u,
                    is_primary: checkedUsersIds.includes(u.id) ? 1 : 0,
                })),
            );
            setCheckedUsers([]);
            setChangesMade(true);
        } else {
            popup('error', USER.MAXIMUM_PRIMARY_USER);
        }
    };

    const handleDeleteUser = () => {
        setUsers(users.filter((u) => !checkedUsers.some((cu) => cu.id === u.id)));
        setCheckedUsers([]);
        setChangesMade(true);
    };

    const handleCheckboxChange = (user) => {
        if (checkedUsers.some((cu) => cu.id === user.id)) {
            setCheckedUsers(checkedUsers.filter((cu) => cu.id !== user.id));
        } else {
            setCheckedUsers([...checkedUsers, user]);
        }
    };

    const handleSaveChanges = () => {
        setOpenConfirmationBox(true);
    };

    const addUserMutation = useMutation({
        mutationFn: async ({ allUsers, newUsers }) =>
            await createAccountUser({
                users: allUsers,
                new_users: newUsers,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, accountId],
            });
            setChangesMade(false);
            setEditUser(false);
            popup('success', USER.USERS_UPDATED);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: async ({ body }) => await deleteAccountUser({ data: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, accountId],
            });
            setChangesMade(false);
            setEditUser(false);
            popup('success', USER.USERS_DELETED);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const saveHandler = (confirm) => {
        if (confirm) {
            if (users.length) {
                const new_users = users.filter(
                    (user) => !data.account.users.some((u) => u.id === user.id),
                );
                const allUsers = users.map((user) => ({
                    account_id: accountId,
                    user_id: user.id,
                    is_primary: primaryUsers.includes(user?.id) ? 1 : 0,
                }));
                addUserMutation.mutate({ allUsers, newUsers: new_users });
            } else if (users.length === 0) {
                deleteUserMutation.mutate({
                    body: {
                        account_id: accountId,
                    },
                });
            }
        }
    };

    const handleEdit = () => {
        setEditUser(true);
    };
    const handleClose = () => {
        setUsers(data.account.users ? data.account.users : []); // reset the users to initial state
        setEditUser(false);
        setNewUser(false);
        setChangesMade(false);
        setCheckedUsers([]);
    };

    const impersonateMutation = useMutation({
        mutationFn: async (user) =>
            await impersonateUser({
                userId: user.id,
            }),
        onSuccess: (res) => {
            const queryString = new URLSearchParams({
                impersonate_token: res?.data?.impersonate_token,
                user_id: res?.data?.user_id,
                refresh_token: res?.data?.refresh_token,
                role: ROLES.USER,
                account_id: accountId,
            }).toString();
            // eslint-disable-next-line no-undef
            const impersonateBaseUrl = process.env.REACT_APP_CUSTOMER_PORTAL_LINK;
            window.open(`${impersonateBaseUrl}/customer/dashboard?${queryString}`, '_blank');
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const impersonateHandler = (user) => {
        if (allowedImpersonate) {
            impersonateMutation.mutate(user);
        } else {
            popup('error', 'Only an admin or CSM can impersonate a user.');
        }
    };

    const removeUser = (id) => {
        if (id) {
            setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
            setChangesMade(true);
        }
    };

    const updateUsersCache = (users) => {
        const cacheExist = !isEmpty(
            queryClient.getQueryData([QUERY_KEYS.ACCOUNT_BY_ID, accountId]),
        );
        // optimistic update
        if (cacheExist) {
            queryClient.setQueryData([QUERY_KEYS.ACCOUNT_BY_ID, accountId], (old) =>
                old
                    ? {
                          ...old,
                          data: {
                              ...old.data,
                              account: {
                                  ...old.data.account,
                                  users: users,
                              },
                          },
                      }
                    : old,
            );
        } else {
            queryClient.invalidateQueries({
                queryKey: [[QUERY_KEYS.ACCOUNT_BY_ID, accountId]],
            });
        }
    };

    const changeUserStatusMutation = useMutation({
        mutationFn: async () =>
            await updateUsersStatusAPI({
                ids: [selectedRow?.id],
                status: selectedRow?.toggle_value,
                sendEmail: true,
            }),
        onSuccess: () => {
            const newUsers = users.map((u) => {
                if (u.id === selectedRow?.id) u.status = selectedRow?.toggle_value ? 1 : 0;
                return u;
            });
            // optimistic update
            updateUsersCache(newUsers);
            setSelectedRow(null);
            popup(
                'success',
                `${selectedRow?.full_name}'s account has been ${selectedRow?.toggle_value ? 'activated' : 'deactivated'}`,
            );
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });
    const changeUserRoleMutation = useMutation({
        mutationFn: async () =>
            await patchAccountUserIsPrimaryAPI({
                user_id: selectedRow?.id,
                is_primary: selectedRow?.is_primary ? 'false' : 'true',
                account_id: data?.account?.account_id,
            }),
        onSuccess: ({ data: res }) => {
            const newUsers = users.map((u) => {
                if (u.id === selectedRow?.id) u.is_primary = res;
                return u;
            });
            updateUsersCache(newUsers);
            popup(
                'success',
                `${selectedRow?.full_name}'s is now a ${selectedRow?.is_primary ? 'secondary user.' : 'primary user.'}`,
            );
            setSelectedRow(null);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const passwordResetEmailMutation = useMutation({
        mutationFn: async () =>
            await initiateResetPassword({
                body: { email: selectedRow?.email },
                headers: {
                    // eslint-disable-next-line no-undef
                    [HEADERS.X_FRONTEND_URL]: process.env.REACT_APP_CUSTOMER_PORTAL_LINK,
                    [HEADERS.X_RESET_PASSWORD_ROUTE]: ROUTES.CUSTOMER.RESET_PASSWORD,
                },
            }),
        onSuccess: () => {
            setSelectedRow(null);
            popup('success', `Password reset link has been sent to ${selectedRow?.email}`);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const onConfirmHandler = (confirm) => {
        if (confirm) {
            if (selectedRow.func_type === 'role') {
                changeUserRoleMutation.mutate();
            }
            if (selectedRow.func_type === 'status') {
                changeUserStatusMutation.mutate();
            }
            if (selectedRow.func_type === 'password_reset') {
                passwordResetEmailMutation.mutate();
            }
        }
    };

    const handleSelectedUser = (user) => {
        setSelectedRow({
            ...user,
            confirmation_message:
                user?.is_primary === 1
                    ? `Are you sure you want to make ${user?.full_name} a secondary user?`
                    : `Are you sure you want to make  ${user?.full_name} a primary user?`,
            func_type: 'role',
        });
        setOpenIsPrimaryBox(true);
    };

    const handleToggleUserStatus = (user, value) => {
        if (!isAdminRole(role)) {
            popup('error', 'Only an admin can activate or deactivate a user.');
            return;
        }
        setSelectedRow({
            ...user,
            confirmation_message:
                value === true ? (
                    <>
                        Are you sure you want to make {user?.full_name}{' '}
                        <span className='text-success fw-bold'>active</span>, and allowing them to
                        access the cloud?
                    </>
                ) : (
                    <>
                        Are you sure you want to make {user?.full_name}{' '}
                        <span className='text-danger fw-bold'>inactive</span>, and disallowing them
                        to access the cloud?
                    </>
                ),
            toggle_value: value,
            func_type: 'status',
        });
        setOpenIsPrimaryBox(true);
    };

    const handlePasswordReset = (user) => {
        if (['0', 0, false, 'false'].includes(user?.status)) {
            popup('error', 'User is not active. Please activate the user first.');
            return;
        }
        setSelectedRow({
            ...user,
            confirmation_message: `Are you sure you want to send ${user?.full_name} a password reset email?`,
            func_type: 'password_reset',
        });
        setOpenIsPrimaryBox(true);
    };

    const onUserSettingClick = (user) => {
        setUserSettingData(user);
        setOpenUserSetting(true);
    };

    return (
        <div className='row'>
            <div className='col-12'>
                <div className='ps-lg-3 d-flex flex-column'>
                    <div className='tabs-set'>
                        <div className='d-flex justify-content-end '>
                            <div style={{ height: '14px' }}>
                                {[
                                    ROLES.SUPER_ADMIN,
                                    ROLES.ADMIN,
                                    ROLES.CONTENT_MANAGER,
                                    ROLES.CSM,
                                ].includes(role) &&
                                    !editUser && (
                                        <span title='Edit' id='action-btn' onClick={handleEdit}>
                                            <Edit className='cursor-pointer' />
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div className={`h-auto ${editUser ? 'hollow-box' : ''} mt-4`}>
                            {editUser && (
                                <div
                                    className={`wrapper position-relative d-flex flex-column align-items-start`}
                                    id='wrapper'
                                >
                                    <div className='w-100 d-flex'>
                                        <div className='w-100 d-flex gap-3 align-items-center justify-content-start flex-wrap flex-grow-1 p-2 p-md-3'>
                                            <div className={isSmallScreen ? 'w-75' : 'w-50'}>
                                                <SSCSelect
                                                    placeholder={'Select Existing User'}
                                                    options={userOptions}
                                                    onInputChange={setSearchUser}
                                                    onChange={(d) => setSelectedUser(d?.value)}
                                                    // Custom search by name and email
                                                    filterOption={(option, inputValue) => {
                                                        return (
                                                            option.label
                                                                .toLowerCase()
                                                                .includes(
                                                                    inputValue.toLowerCase(),
                                                                ) ||
                                                            option.value.email
                                                                .toLowerCase()
                                                                .includes(inputValue.toLowerCase())
                                                        );
                                                    }}
                                                    isClearable
                                                    isLoading={isFetching}
                                                />
                                            </div>
                                            {isSmallScreen ? (
                                                <span
                                                    id='action-btn-green'
                                                    title={'Add New User'}
                                                    onClick={() => setNewUser(!newUser)}
                                                >
                                                    <IconPlus />
                                                </span>
                                            ) : (
                                                <span className='flex h-100 ms-3 align-align-items-center pt-1'>
                                                    <button
                                                        className='ssc-primary-green-btn button-green-filled mt-0 mb-0'
                                                        onClick={() => setNewUser(!newUser)}
                                                    >
                                                        Add New User
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                        <div className='me-3 d-flex align-items-center justify-content-center'>
                                            <button
                                                type='button'
                                                className='btn-close'
                                                data-bs-dismiss='modal'
                                                aria-label='Close'
                                                onClick={handleClose}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {editUser && users.length !== 0 && checkedUsers.length > 0 && (
                                <div
                                    className='mt-2 input-set w-100 d-flex justify-content-end align-items-center gap-3'
                                    style={{ height: '40px' }}
                                >
                                    <DeleteButton
                                        handleClick={handleDeleteUser}
                                        width={20}
                                        height={20}
                                    />

                                    {isSmallScreen ? (
                                        <span className='me-3'>
                                            <button
                                                type='button'
                                                id='action-btn-green'
                                                title={USER.SET_AS_PRIMARY_USER}
                                                onClick={handleSetPrimary}
                                                disabled={
                                                    checkedUsers.every(
                                                        (user) => user.is_primary === 1,
                                                    ) ||
                                                    checkedUsers?.length > MAX_LENGTH.PRIMARY_USERS
                                                }
                                            >
                                                <User stroke='#fff' height={18} />
                                            </button>
                                        </span>
                                    ) : (
                                        <button
                                            type='button'
                                            className='ssc-primary-green-btn button-green-filled me-3'
                                            onClick={handleSetPrimary}
                                            disabled={
                                                checkedUsers.every(
                                                    (user) => user.is_primary === 1,
                                                ) || checkedUsers?.length > MAX_LENGTH.PRIMARY_USERS
                                            }
                                        >
                                            {USER.SET_AS_PRIMARY_USER}
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className='scroll-create-user mt-3 flex-fill flex-grow-1'>
                                {newUser && (
                                    <CreateUser
                                        setAddNewUser={setAddNewUser}
                                        setNewUser={setNewUser}
                                    />
                                )}
                                {users?.length === 0 && !newUser && (
                                    <div
                                        className='mt-4 vh-50 d-flex justify-content-center'
                                        style={{ fontSize: '16px' }}
                                    >
                                        {INFO.NO_USER_FOUND}
                                    </div>
                                )}
                                {users?.length > 0 && (
                                    <table className='table'>
                                        <thead
                                            className='position-sticky'
                                            style={{ top: 0, zIndex: 99 }}
                                        >
                                            <tr>
                                                {getTableHeader(editUser).map((h, i) => {
                                                    return (
                                                        <th
                                                            key={i}
                                                            className={`fw-normal lh-1 text-secondary pb-2 px-2 text-nowrap + ${i === 1 && ' sticky-th-left left-neg-16 '}`}
                                                            style={{ fontSize: 13 }}
                                                        >
                                                            {h}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {users
                                                .filter((user) => user.code !== ROLES.CSM)
                                                .map((user, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            {editUser ? (
                                                                <input
                                                                    type='checkbox'
                                                                    className='cursor-pointer'
                                                                    checked={checkedUsers.some(
                                                                        (cu) => cu.id === user.id,
                                                                    )}
                                                                    onChange={() =>
                                                                        handleCheckboxChange(user)
                                                                    }
                                                                />
                                                            ) : (
                                                                <span>{index + 1}.</span>
                                                            )}
                                                        </td>
                                                        {editUser && <td>{index + 1}</td>}
                                                        <td
                                                            className='sticky-td-left text-nowrap'
                                                            style={{ left: '-16px' }}
                                                        >
                                                            {user.full_name}
                                                        </td>
                                                        <td>{user.email}</td>
                                                        <td>
                                                            {formatDate(user?.last_logged_in_at)}
                                                        </td>
                                                        <td>
                                                            {user.is_primary === 1
                                                                ? USER.PRIMARY_USER_BUTTON
                                                                : USER.SECONDARY_USER}
                                                        </td>
                                                        {!editUser && (
                                                            <td>
                                                                <SSCToggle
                                                                    checked={Boolean(user.status)}
                                                                    title={
                                                                        user.status
                                                                            ? 'Active: User can access the cloud'
                                                                            : 'Inactive: User cloud access is suspended'
                                                                    }
                                                                    onChange={(value) =>
                                                                        handleToggleUserStatus(
                                                                            user,
                                                                            value,
                                                                        )
                                                                    }
                                                                    isLoading={
                                                                        changeUserStatusMutation.isPending &&
                                                                        selectedRow.id === user.id
                                                                    }
                                                                />
                                                            </td>
                                                        )}
                                                        {!editUser && (
                                                            <td>
                                                                <div className='d-flex gap-2'>
                                                                    {!editUser &&
                                                                        isCSMRole(role) &&
                                                                        (passwordResetEmailMutation.isPending &&
                                                                        selectedRow.id ===
                                                                            user.id ? (
                                                                            <CircleSpinner size='1.85rem' />
                                                                        ) : (
                                                                            <button
                                                                                title={
                                                                                    'Send Password Reset Link Email'
                                                                                }
                                                                                id='action-btn'
                                                                                type='button'
                                                                                onClick={() =>
                                                                                    handlePasswordReset(
                                                                                        user,
                                                                                    )
                                                                                }
                                                                                aria-label={`Password Reset Link to ${user.full_name}`}
                                                                            >
                                                                                <Email
                                                                                    width={18}
                                                                                    height={18}
                                                                                />
                                                                            </button>
                                                                        ))}
                                                                    {uptoContentManagerRole(
                                                                        role,
                                                                    ) && (
                                                                        <button
                                                                            title={
                                                                                user.is_primary ===
                                                                                1
                                                                                    ? 'Set as Secondary'
                                                                                    : 'Set as Primary'
                                                                            }
                                                                            id='action-btn'
                                                                            type='button'
                                                                            onClick={() =>
                                                                                handleSelectedUser(
                                                                                    user,
                                                                                )
                                                                            }
                                                                            aria-label={`Change user role`}
                                                                        >
                                                                            <ProfileIcon
                                                                                width={18}
                                                                                height={18}
                                                                            />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        title={'Impersonate user'}
                                                                        id='action-btn'
                                                                        type='button'
                                                                        onClick={() =>
                                                                            impersonateHandler(user)
                                                                        }
                                                                        aria-label={`Impersonate ${user.full_name}`}
                                                                        disabled={
                                                                            impersonateMutation.isPending
                                                                        }
                                                                    >
                                                                        <ImpersonateUserIcon />
                                                                    </button>
                                                                    {isCSMRole(role) &&
                                                                        Boolean(
                                                                            user.is_primary,
                                                                        ) && (
                                                                            <button
                                                                                title={
                                                                                    'User Settings'
                                                                                }
                                                                                id='action-btn'
                                                                                type='button'
                                                                                onClick={() =>
                                                                                    onUserSettingClick(
                                                                                        user,
                                                                                    )
                                                                                }
                                                                                aria-label={`${user.full_name}'s Email Notification Settings`}
                                                                            >
                                                                                <SettingIcon />
                                                                            </button>
                                                                        )}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {editUser && (
                                                            <td className='d-flex gap-2'>
                                                                <button
                                                                    title='Remove'
                                                                    id='action-btn-red'
                                                                    type='button'
                                                                    onClick={() =>
                                                                        removeUser(user?.id)
                                                                    }
                                                                    aria-label={`Remove ${user.full_name}`}
                                                                >
                                                                    <Delete stroke='#ff004d' />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                )}
                                {openConfirmationBox && (
                                    <ConfirmationBox
                                        setConfirm={(v) => saveHandler(v)}
                                        openConfirmationBox={openConfirmationBox}
                                        setOpenConfirmationBox={setOpenConfirmationBox}
                                        customMessage={USER.ARE_YOU_SURE}
                                    />
                                )}
                                {openIsPrimaryBox && (
                                    <ConfirmationBox
                                        setConfirm={(v) => onConfirmHandler(v)}
                                        openConfirmationBox={openIsPrimaryBox}
                                        setOpenConfirmationBox={setOpenIsPrimaryBox}
                                        customMessage={selectedRow.confirmation_message}
                                    />
                                )}
                            </div>
                            {changesMade && (
                                <div className='ms-auto m-4 d-flex justify-content-end '>
                                    <button
                                        className='ssc-primary-green-btn button-green-filled mt-0 mb-0'
                                        onClick={handleSaveChanges}
                                        disabled={
                                            addUserMutation.isPending ||
                                            deleteUserMutation.isPending
                                        }
                                    >
                                        {(addUserMutation.isPending ||
                                            deleteUserMutation.isPending) && (
                                            <span className='me-1'>
                                                <CircleSpinner />
                                            </span>
                                        )}
                                        {AUTH.SAVE_CHANGES}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {openUserSetting && (
                <Modal
                    show={openUserSetting}
                    onHide={() => {
                        setOpenUserSetting(false);
                        setUserSettingData(undefined);
                    }}
                    size='md'
                    dialogClassName='overflow-hidden'
                    centered
                    key={accountId}
                >
                    <Modal.Header closeButton>
                        <Modal.Title style={{ fontSize: 'var(--font-18)' }}>
                            User Notification Preferences
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <NotificationSettings id={userSettingData.id} />
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

ManageUserBox.propTypes = {
    data: PropTypes.object.isRequired,
};
